import React, { useState } from 'react';
import { 
  X, 
  Search, 
  ChevronDown, 
  ChevronRight, 
  ArrowRight, 
  Globe, 
  Percent, 
  Tag, 
  TrendingUp, 
  DollarSign, 
  UtensilsCrossed, 
  Users, 
  Receipt, 
  Clock, 
  Banknote, 
  Calculator, 
  CreditCard, 
  FileText, 
  Building2, 
  RefreshCw, 
  LineChart, 
  ArrowRightLeft, 
  Home, 
  Calendar, 
  TrendingDown, 
  PiggyBank, 
  Wallet, 
  UserCheck, 
  Flame,
  Bookmark
} from 'lucide-react';
import { AppView, RegionInfo, ToolCategory, ToolItem } from '../../types';
import { YamooPayLogo } from '../common/YamooPayLogo';

interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: AppView) => void;
  onSelectTool: (toolId: string) => void;
  selectedRegion: RegionInfo;
  onSelectRegion: (reg: RegionInfo) => void;
  allRegions: RegionInfo[];
  tools: ToolItem[];
  onOpenSearch: () => void;
  onOpenWorkspace: () => void;
  onOpenAuthModal: () => void;
  isLoggedIn: boolean;
}

const CANONICAL_CATEGORIES: { id: ToolCategory; label: string }[] = [
  { id: 'Everyday Money', label: 'Everyday Money' },
  { id: 'Income & Tax', label: 'Income & Tax' },
  { id: 'Currency & Transfers', label: 'Currency & Transfers' },
  { id: 'Borrow', label: 'Borrow' },
  { id: 'Save & Invest', label: 'Save & Invest' },
];

export const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({
  isOpen,
  onClose,
  onNavigate,
  onSelectTool,
  selectedRegion,
  onSelectRegion,
  allRegions,
  tools,
  onOpenSearch,
  onOpenWorkspace,
  onOpenAuthModal,
  isLoggedIn,
}) => {
  const [expandedCategory, setExpandedCategory] = useState<ToolCategory | null>('Everyday Money');
  const [regionSelectorOpen, setRegionSelectorOpen] = useState(false);

  if (!isOpen) return null;

  const publishedTools = tools.filter((t) => (t.status ? t.status === 'PUBLISHED' : true));

  const toggleCategory = (catId: ToolCategory) => {
    setExpandedCategory((prev) => (prev === catId ? null : catId));
  };

  const renderToolIcon = (iconName: string) => {
    const props = { className: 'w-3.5 h-3.5 text-[#2563EB] shrink-0' };
    switch (iconName) {
      case 'Percent':
        return <Percent {...props} />;
      case 'Tag':
        return <Tag {...props} />;
      case 'TrendingUp':
        return <TrendingUp {...props} />;
      case 'DollarSign':
        return <DollarSign {...props} />;
      case 'UtensilsCrossed':
        return <UtensilsCrossed {...props} />;
      case 'Users':
        return <Users {...props} />;
      case 'Receipt':
        return <Receipt {...props} />;
      case 'Clock':
        return <Clock {...props} />;
      case 'Banknote':
        return <Banknote {...props} />;
      case 'Calculator':
        return <Calculator {...props} />;
      case 'CreditCard':
        return <CreditCard {...props} />;
      case 'FileText':
        return <FileText {...props} />;
      case 'Building2':
        return <Building2 {...props} />;
      case 'RefreshCw':
        return <RefreshCw {...props} />;
      case 'LineChart':
        return <LineChart {...props} />;
      case 'ArrowRightLeft':
        return <ArrowRightLeft {...props} />;
      case 'Home':
        return <Home {...props} />;
      case 'Calendar':
        return <Calendar {...props} />;
      case 'TrendingDown':
        return <TrendingDown {...props} />;
      case 'PiggyBank':
        return <PiggyBank {...props} />;
      case 'Wallet':
        return <Wallet {...props} />;
      case 'UserCheck':
        return <UserCheck {...props} />;
      case 'Flame':
        return <Flame {...props} />;
      default:
        return <TrendingUp {...props} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex justify-end animate-in fade-in duration-200">
      <div className="w-full max-w-sm bg-white h-full overflow-y-auto flex flex-col justify-between shadow-2xl animate-in slide-in-from-right duration-200">
        <div className="p-5 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-4">
            <YamooPayLogo size="sm" />
            <button
              onClick={onClose}
              className="p-1.5 text-[#64748B] hover:text-[#1E293B] hover:bg-[#F1F5F9] rounded-lg transition-colors cursor-pointer"
              aria-label="Close navigation drawer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Quick Search */}
          <button
            onClick={() => {
              onClose();
              onOpenSearch();
            }}
            className="w-full flex items-center justify-between px-3.5 py-2.5 bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#CBD5E1] rounded-xl text-xs font-medium text-[#64748B] transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-[#2563EB]" />
              <span>Search 25+ financial tools...</span>
            </div>
            <kbd className="px-1.5 py-0.5 text-[10px] font-mono font-semibold bg-white rounded border border-[#E2E8F0] text-[#94A3B8]">
              ⌘K
            </kbd>
          </button>

          {/* Main Navigation Links */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] px-2 py-1">
              Navigation
            </div>
            <button
              onClick={() => {
                onNavigate('homepage');
                onClose();
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-[#1E293B] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
            >
              Home
            </button>
            <button
              onClick={() => {
                onNavigate('currency-intelligence');
                onClose();
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-[#1E293B] hover:bg-[#F8FAFC] flex items-center justify-between transition-colors cursor-pointer"
            >
              <span>Money & Exchange Rates</span>
              <span className="text-[10px] font-bold bg-[#EFF6FF] text-[#2563EB] px-2 py-0.5 rounded-full">
                170+ FX
              </span>
            </button>
            <button
              onClick={() => {
                onNavigate('financial-guides');
                onClose();
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-[#1E293B] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
            >
              Insights & Guides
            </button>
            <button
              onClick={() => {
                onNavigate('developer-portal');
                onClose();
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-[#1E293B] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
            >
              Developers & API
            </button>
            <button
              onClick={() => {
                onNavigate('discovery');
                onClose();
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-[#1E293B] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
            >
              About YamooPay
            </button>
          </div>

          {/* Tools & Calculators Canonical Categories (Accordion) */}
          <div className="space-y-2 pt-2 border-t border-[#E2E8F0]">
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">
                Tools & Calculators
              </span>
              <button
                onClick={() => {
                  onNavigate('tools');
                  onClose();
                }}
                className="text-[11px] font-bold text-[#2563EB] hover:underline flex items-center gap-1"
              >
                <span>View All</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            <div className="space-y-1.5">
              {CANONICAL_CATEGORIES.map((cat) => {
                const isExpanded = expandedCategory === cat.id;
                const catTools = publishedTools.filter((t) => t.category === cat.id);

                return (
                  <div
                    key={cat.id}
                    className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-[#F8FAFC]/50 transition-colors"
                  >
                    <button
                      onClick={() => toggleCategory(cat.id)}
                      className="w-full flex items-center justify-between p-3 text-left font-bold text-xs text-[#1E293B] hover:bg-[#F1F5F9] transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB]"></span>
                        <span>{cat.label}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[#64748B]">
                        <span className="text-[10px] font-medium opacity-75">{catTools.length}</span>
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180 text-[#2563EB]' : ''
                          }`}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 space-y-1 bg-white border-t border-[#E2E8F0] animate-in fade-in duration-150">
                        {catTools.map((tool) => (
                          <button
                            key={tool.id}
                            onClick={() => {
                              onSelectTool(tool.id);
                              onClose();
                            }}
                            className="w-full text-left flex items-start gap-2.5 p-2 rounded-lg hover:bg-[#EFF6FF] transition-colors group cursor-pointer"
                          >
                            <div className="mt-0.5 w-5 h-5 rounded-sm bg-[#EFF6FF] flex items-center justify-center">
                              {renderToolIcon(tool.iconName)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-bold text-[#1E293B] group-hover:text-[#2563EB] transition-colors truncate">
                                {tool.name || tool.label}
                              </div>
                              <div className="text-[10px] text-[#64748B] truncate">
                                {tool.subtitle || tool.tagline}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer Area: Region Selector & Workspace Action */}
        <div className="p-5 border-t border-[#E2E8F0] bg-[#F8FAFC] space-y-3">
          {/* Region Switcher */}
          <div className="relative">
            <button
              onClick={() => setRegionSelectorOpen(!regionSelectorOpen)}
              className="w-full flex items-center justify-between px-3 py-2 bg-white border border-[#E2E8F0] rounded-xl text-xs font-medium text-[#1E293B] hover:bg-[#F1F5F9] transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm">{selectedRegion.flag}</span>
                <span>{selectedRegion.name}</span>
              </div>
              <div className="flex items-center gap-1 text-[#64748B] font-mono text-[11px]">
                <span>{selectedRegion.currency}</span>
                <ChevronRight className="w-3 h-3" />
              </div>
            </button>

            {regionSelectorOpen && (
              <div className="mt-2 bg-white border border-[#E2E8F0] rounded-xl shadow-lg p-1.5 space-y-0.5 max-h-48 overflow-y-auto">
                {allRegions.map((reg) => (
                  <button
                    key={reg.code}
                    onClick={() => {
                      onSelectRegion(reg);
                      setRegionSelectorOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                      selectedRegion.code === reg.code
                        ? 'bg-[#1E293B] text-white font-bold'
                        : 'hover:bg-[#F1F5F9] text-[#1E293B]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{reg.flag}</span>
                      <span>{reg.name}</span>
                    </div>
                    <span className="font-mono text-[10px] opacity-80">{reg.currency}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User Workspace or Sign In */}
          {isLoggedIn ? (
            <button
              onClick={() => {
                onClose();
                onOpenWorkspace();
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-xs"
            >
              <Bookmark className="w-3.5 h-3.5" />
              <span>Open My YamooPay Workspace</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  onClose();
                  onOpenAuthModal();
                }}
                className="flex-1 py-2.5 bg-white border border-[#E2E8F0] hover:bg-[#F1F5F9] text-[#1A1A1A] rounded-xl text-xs font-bold transition-colors cursor-pointer"
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  onClose();
                  onOpenAuthModal();
                }}
                className="flex-1 py-2.5 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-xs"
              >
                Open Account
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
